/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * REST API を使用した組織管理 hooks
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type Organization = components["schemas"]["Organization"];
export type User = components["schemas"]["User"];
export type CreateOrganizationInput =
  components["schemas"]["CreateOrganizationInput"];
export type UpdateOrganizationInput =
  components["schemas"]["UpdateOrganizationInput"];
export type AssignUserToOrganizationInput =
  components["schemas"]["AssignUserToOrganizationInput"];

// =====================================
// useOrganizationsRestApi - 組織一覧取得
// =====================================

export interface UseOrganizationsRestApiOptions {
  onCompleted?: () => void;
}

export interface UseOrganizationsRestApiResult {
  data: { organizations: Organization[] } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<Organization[]>;
}

/**
 * 組織一覧を取得するhook
 * @param {UseOrganizationsRestApiOptions} options - オプション設定
 * @return {UseOrganizationsRestApiResult} 組織一覧、エラー、ローディング状態、refetch関数
 */
export function useOrganizationsRestApi(
  options: UseOrganizationsRestApiOptions = {},
): UseOrganizationsRestApiResult {
  const [data, setData] = useState<
    { organizations: Organization[] } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchOrganizations = useCallback(async (): Promise<Organization[]> => {
    setLoading(true);
    setError(undefined);

    try {
      const { data: responseData, error: apiError } =
        await api.GET("/api/organizations");

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || "API error",
        );
      }

      if (!responseData) {
        throw new Error("No data received");
      }

      setData({ organizations: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.onCompleted]);

  useEffect(() => {
    fetchOrganizations().catch(() => {});
  }, [fetchOrganizations]);

  return {
    data,
    error,
    loading,
    refetch: fetchOrganizations,
  };
}

// =====================================
// useOrganizationRestApi - 組織詳細取得
// =====================================

export interface UseOrganizationRestApiOptions {
  id: string;
  skip?: boolean;
  onCompleted?: () => void;
}

export interface UseOrganizationRestApiResult {
  data: { organization: Organization } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<Organization>;
}

/**
 * 組織詳細を取得するhook
 * @param {UseOrganizationRestApiOptions} options - オプション設定（id必須）
 * @return {UseOrganizationRestApiResult} 組織詳細、エラー、ローディング状態、refetch関数
 */
export function useOrganizationRestApi(
  options: UseOrganizationRestApiOptions,
): UseOrganizationRestApiResult {
  const [data, setData] = useState<{ organization: Organization } | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchOrganization = useCallback(async (): Promise<Organization> => {
    setLoading(true);
    setError(undefined);

    try {
      const { data: responseData, error: apiError } = await api.GET(
        "/api/organizations/{id}",
        {
          params: { path: { id: options.id } },
        },
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || "API error",
        );
      }

      if (!responseData) {
        throw new Error("No data received");
      }

      setData({ organization: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.id, options.onCompleted]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchOrganization().catch(() => {});
    }
  }, [options.skip, options.id, fetchOrganization]);

  return {
    data,
    error,
    loading,
    refetch: fetchOrganization,
  };
}

// =====================================
// useOrganizationUsersRestApi - 組織所属ユーザー取得
// =====================================

export interface UseOrganizationUsersRestApiOptions {
  organizationId: string;
  skip?: boolean;
  onCompleted?: () => void;
}

export interface UseOrganizationUsersRestApiResult {
  data: { organizationUsers: User[] } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<User[]>;
}

/**
 * 組織所属ユーザー一覧を取得するhook
 * @param {UseOrganizationUsersRestApiOptions} options - オプション設定（organizationId必須）
 * @return {UseOrganizationUsersRestApiResult} ユーザー一覧、エラー、ローディング状態、refetch関数
 */
export function useOrganizationUsersRestApi(
  options: UseOrganizationUsersRestApiOptions,
): UseOrganizationUsersRestApiResult {
  const [data, setData] = useState<{ organizationUsers: User[] } | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchUsers = useCallback(async (): Promise<User[]> => {
    setLoading(true);
    setError(undefined);

    try {
      const { data: responseData, error: apiError } = await api.GET(
        "/api/organizations/{id}/users",
        {
          params: { path: { id: options.organizationId } },
        },
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || "API error",
        );
      }

      if (!responseData) {
        throw new Error("No data received");
      }

      setData({ organizationUsers: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.organizationId, options.onCompleted]);

  useEffect(() => {
    if (!options.skip && options.organizationId) {
      fetchUsers().catch(() => {});
    }
  }, [options.skip, options.organizationId, fetchUsers]);

  return {
    data,
    error,
    loading,
    refetch: fetchUsers,
  };
}

// =====================================
// useUnassignedUsersRestApi - 未割り当てユーザー取得
// =====================================

export interface UseUnassignedUsersRestApiOptions {
  skip?: boolean;
  onCompleted?: () => void;
}

export interface UseUnassignedUsersRestApiResult {
  data: { unassignedUsers: User[] } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<User[]>;
}

/**
 * 未割り当てユーザー一覧を取得するhook
 * @param {UseUnassignedUsersRestApiOptions} options - オプション設定
 * @return {UseUnassignedUsersRestApiResult} ユーザー一覧、エラー、ローディング状態、refetch関数
 */
export function useUnassignedUsersRestApi(
  options: UseUnassignedUsersRestApiOptions = {},
): UseUnassignedUsersRestApiResult {
  const [data, setData] = useState<{ unassignedUsers: User[] } | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchUnassignedUsers = useCallback(async (): Promise<User[]> => {
    setLoading(true);
    setError(undefined);

    try {
      const { data: responseData, error: apiError } = await api.GET(
        "/api/organizations/users/unassigned",
      );

      if (apiError) {
        throw new Error(
          (apiError as { message?: string }).message || "API error",
        );
      }

      if (!responseData) {
        throw new Error("No data received");
      }

      setData({ unassignedUsers: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.onCompleted]);

  useEffect(() => {
    if (!options.skip) {
      fetchUnassignedUsers().catch(() => {});
    }
  }, [options.skip, fetchUnassignedUsers]);

  return {
    data,
    error,
    loading,
    refetch: fetchUnassignedUsers,
  };
}

// =====================================
// useCreateOrganizationRestApi - 組織作成
// =====================================

export interface UseCreateOrganizationRestApiResult {
  createOrganization: (input: CreateOrganizationInput) => Promise<Organization>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 組織を作成するhook
 * @return {UseCreateOrganizationRestApiResult} 作成関数、ローディング状態、エラー
 */
export function useCreateOrganizationRestApi(): UseCreateOrganizationRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createOrganization = useCallback(
    async (input: CreateOrganizationInput): Promise<Organization> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.POST(
          "/api/organizations",
          {
            body: input,
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createOrganization,
    loading,
    error,
  };
}

// =====================================
// useUpdateOrganizationRestApi - 組織更新
// =====================================

export interface UseUpdateOrganizationRestApiResult {
  updateOrganization: (
    id: string,
    input: UpdateOrganizationInput,
  ) => Promise<Organization>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 組織を更新するhook
 * @return {UseUpdateOrganizationRestApiResult} 更新関数、ローディング状態、エラー
 */
export function useUpdateOrganizationRestApi(): UseUpdateOrganizationRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateOrganization = useCallback(
    async (
      id: string,
      input: UpdateOrganizationInput,
    ): Promise<Organization> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.PUT(
          "/api/organizations/{id}",
          {
            params: { path: { id } },
            body: input,
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateOrganization,
    loading,
    error,
  };
}

// =====================================
// useDeleteOrganizationRestApi - 組織削除
// =====================================

export interface UseDeleteOrganizationRestApiResult {
  deleteOrganization: (
    id: string,
  ) => Promise<{ success: boolean; message: string }>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 組織を削除するhook
 * @return {UseDeleteOrganizationRestApiResult} 削除関数、ローディング状態、エラー
 */
export function useDeleteOrganizationRestApi(): UseDeleteOrganizationRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteOrganization = useCallback(
    async (id: string): Promise<{ success: boolean; message: string }> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.DELETE(
          "/api/organizations/{id}",
          {
            params: { path: { id } },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    deleteOrganization,
    loading,
    error,
  };
}

// =====================================
// useAssignUserToOrganizationRestApi - ユーザーを組織に割り当て
// =====================================

export interface AssignUserToOrganizationResponse {
  success: boolean;
  message: string;
  user: User;
}

export interface UseAssignUserToOrganizationRestApiResult {
  assignUserToOrganization: (
    input: AssignUserToOrganizationInput,
  ) => Promise<AssignUserToOrganizationResponse>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * ユーザーを組織に割り当てるhook
 * @return {UseAssignUserToOrganizationRestApiResult} 割り当て関数、ローディング状態、エラー
 */
export function useAssignUserToOrganizationRestApi(): UseAssignUserToOrganizationRestApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const assignUserToOrganization = useCallback(
    async (
      input: AssignUserToOrganizationInput,
    ): Promise<AssignUserToOrganizationResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const { data: responseData, error: apiError } = await api.POST(
          "/api/organizations/users/assign",
          {
            body: input,
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        return responseData as AssignUserToOrganizationResponse;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    assignUserToOrganization,
    loading,
    error,
  };
}

export default {
  useOrganizationsRestApi,
  useOrganizationRestApi,
  useOrganizationUsersRestApi,
  useUnassignedUsersRestApi,
  useCreateOrganizationRestApi,
  useUpdateOrganizationRestApi,
  useDeleteOrganizationRestApi,
  useAssignUserToOrganizationRestApi,
};
